import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState({}); // { slug: note }

  const refreshFavorites = useCallback(async () => {
    try {
      const { data } = await api.get("/me/favorites/slugs");
      setFavorites(data || {});
    } catch {
      setFavorites({});
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("ae_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => { setUser(r.data); refreshFavorites(); })
      .catch(() => localStorage.removeItem("ae_token"))
      .finally(() => setLoading(false));
  }, [refreshFavorites]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("ae_token", data.access_token);
      setUser(data.user);
      await refreshFavorites();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      localStorage.setItem("ae_token", data.access_token);
      setUser(data.user);
      setFavorites({});
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* token may be gone */ }
    localStorage.removeItem("ae_token");
    localStorage.removeItem("ae_token_admin");
    setUser(null);
    setFavorites({});
  };

  const startEmulate = async (uid) => {
    const { data } = await api.post(`/admin/users/${uid}/emulate`);
    localStorage.setItem("ae_token_admin", localStorage.getItem("ae_token"));
    localStorage.setItem("ae_token", data.access_token);
    const me = await api.get("/auth/me");
    setUser(me.data);
    await refreshFavorites();
    return me.data;
  };

  const stopEmulate = async () => {
    const adminToken = localStorage.getItem("ae_token_admin");
    if (adminToken) {
      localStorage.setItem("ae_token", adminToken);
      localStorage.removeItem("ae_token_admin");
    }
    const me = await api.get("/auth/me");
    setUser(me.data);
    await refreshFavorites();
  };

  const isFavorite = (slug) => Object.prototype.hasOwnProperty.call(favorites, slug);

  const toggleFavorite = async (slug, note = "") => {
    if (!user) return { ok: false, needAuth: true };
    if (isFavorite(slug)) {
      await api.delete(`/me/favorites/${slug}`);
      setFavorites((f) => { const n = { ...f }; delete n[slug]; return n; });
      return { ok: true, favorited: false };
    }
    await api.post(`/me/favorites/${slug}`, { note });
    setFavorites((f) => ({ ...f, [slug]: { note, status: "wishlist" } }));
    return { ok: true, favorited: true };
  };

  const saveNote = async (slug, note) => {
    if (!user) return { ok: false, needAuth: true };
    if (isFavorite(slug)) {
      await api.put(`/me/favorites/${slug}`, { note });
    } else {
      await api.post(`/me/favorites/${slug}`, { note });
    }
    setFavorites((f) => ({ ...f, [slug]: { note, status: f[slug]?.status || "wishlist" } }));
    return { ok: true };
  };

  const setStatus = async (slug, status) => {
    if (!user) return { ok: false, needAuth: true };
    await api.put(`/me/favorites/${slug}/status`, { status });
    setFavorites((f) => ({ ...f, [slug]: { note: f[slug]?.note || "", status } }));
    return { ok: true };
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, favorites, isFavorite, toggleFavorite, saveNote, setStatus, refreshFavorites, startEmulate, stopEmulate }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
